import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  ClipboardList,
  Ticket,
  Wallet,
  Users,
  HandCoins,
  ShoppingBag,
  FileSignature,
  Gift,
  CalendarCheck,
  Sparkles,
  Star,
} from "lucide-react";

// Hero + illustrative images
const HERO_IMG =
  "https://images.unsplash.com/photo-1529832588601-c01e066263a8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=75";
const IMG_VOL =
  "https://images.unsplash.com/photo-1758599667718-684569efe224?crop=entropy&cs=srgb&fm=jpg&w=1600&q=75";
const IMG_SCHOOL =
  "https://images.unsplash.com/photo-1588075592405-d3d4f0846961?crop=entropy&cs=srgb&fm=jpg&w=1600&q=75";
const IMG_TICKETS =
  "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=75";

const PRODUCTS = [
  {
    icon: ClipboardList,
    title: "Hojas de inscripción",
    tag: "Organiza eventos y voluntarios en minutos.",
    slug: "hojas-de-inscripcion",
  },
  {
    icon: Ticket,
    title: "Boletos",
    tag: "Venta y check-in con código QR desde el celular.",
    slug: "boletos",
  },
  {
    icon: Wallet,
    title: "Cuotas y cobros",
    tag: "Cobra cuotas, inscripciones y formularios con pago.",
    slug: "cuotas",
  },
  {
    icon: Users,
    title: "Recaudación en equipo",
    tag: "Que cada participante arme su propia página de donación.",
    slug: "recaudacion-en-equipo",
  },
  {
    icon: HandCoins,
    title: "Donaciones",
    tag: "Recibe donaciones para tu causa con meta pública.",
    slug: "donaciones",
  },
  {
    icon: ShoppingBag,
    title: "Tienda en línea",
    tag: "Vende playeras, tazas o cualquier producto sin abrir otra tienda.",
    slug: "tienda-en-linea",
  },
  {
    icon: FileSignature,
    title: "Formularios y consentimientos",
    tag: "Crea formularios personalizados y recibe firmas digitales.",
    slug: "formularios-y-consentimientos",
  },
  {
    icon: Gift,
    title: "Regalos grupales",
    tag: "Organiza vaquitas y junta aportaciones para un regalo.",
    slug: "regalos-grupales",
  },
  {
    icon: CalendarCheck,
    title: "Reserva de citas",
    tag: "Entrevistas, asesorías y consultas con horarios definidos.",
    slug: "reserva-de-citas",
  },
];

const USE_CASES = [
  {
    img: IMG_VOL,
    stat1: "300+ voluntarios coordinados",
    stat2: "Turnos sin duplicados",
    subject: "Brigadas comunitarias",
    quote:
      "Con un solo link organizamos los turnos de sábado. Nadie se pisa, todos saben dónde estar.",
  },
  {
    img: IMG_SCHOOL,
    stat1: "12 grupos participando",
    stat2: "$85,000 recaudado",
    subject: "Kermés escolar",
    quote:
      "Cobramos las cuotas del salón y llevamos la lista de qué trae cada mamá sin cadenas interminables.",
  },
  {
    img: IMG_TICKETS,
    stat1: "500 boletos vendidos",
    stat2: "Check-in en 5 segundos",
    subject: "Concierto benéfico",
    quote:
      "Los QR nos salvaron. La entrada al evento fue rapidísima y nadie se coló.",
  },
];

const FAQS = [
  {
    q: "¿Apúntate es gratis?",
    a: "Sí. Empezar es gratis y no pedimos tarjeta. La cuenta gratuita incluye todos los tipos de eventos: hojas de inscripción, boletos, cuotas, vaquitas, formularios y más.",
  },
  {
    q: "¿Los invitados tienen que crear cuenta?",
    a: "No. Cualquier persona con el link puede apuntarse, reservar un horario, hacer una aportación o comprar un boleto sin registrarse. Un solo click y listo.",
  },
  {
    q: "¿Puedo cobrar pagos desde Apúntate?",
    a: "El cobro digital está en camino con Stripe y Mercado Pago. Hoy puedes llevar el registro manual de quién ya pagó, con notas de instrucciones (SPEI, transferencia, efectivo). Cuando actives pagos digitales, se procesan directamente en tu evento.",
  },
  {
    q: "¿Para qué puedo usar Apúntate?",
    a: "Para casi cualquier cosa que involucre organizar personas: potlucks, kermés, cuotas del salón, boletos para el retiro de la iglesia, vaquitas de cumpleaños, agenda de entrevistas, consentimientos firmados, tienda de mercancía del equipo — todo desde una sola cuenta.",
  },
  {
    q: "¿En qué se diferencia de Google Forms?",
    a: "Google Forms recopila información. Apúntate coordina personas: cupos por espacio, cobros, recordatorios (próximamente) y listas de espera, integrados por default.",
  },
  {
    q: "¿Qué tipos de grupos usan Apúntate?",
    a: "Escuelas y mesas directivas, equipos deportivos, iglesias y parroquias, ONGs, empresas y grupos de amigos. Cualquier persona u organización que necesite coordinar gente y (opcionalmente) recolectar dinero en un mismo lugar.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 items-center animate-fade-up">
          <div className="md:col-span-7">
            <span
              className="inline-block text-xs uppercase tracking-[0.24em] text-coral mb-6"
              data-testid="hero-eyebrow"
            >
              Inscripciones y pagos para todo LATAM
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-ink-900 font-light">
              Organiza tu grupo.{" "}
              <em className="italic text-coral">Recibe pagos.</em>
              <br />
              En un solo lugar.
            </h1>
            <p className="mt-6 text-lg text-ink-500 max-w-xl leading-relaxed">
              Hojas de inscripción, boletos, cuotas, vaquitas, donaciones, tienda y más. Cada forma en que tu grupo necesita juntarse — y cobrar — dentro de una sola plataforma.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/registro">
                <Button
                  size="lg"
                  className="rounded-full bg-coral hover:bg-coral-hover text-white px-7 h-12 text-base"
                  data-testid="hero-cta-primary"
                >
                  Empezar gratis
                  <ArrowRight size={18} strokeWidth={1.75} className="ml-2" />
                </Button>
              </Link>
              <a href="#productos">
                <Button
                  variant="ghost"
                  size="lg"
                  className="rounded-full h-12 text-ink-700 hover:bg-cream-100 px-6"
                  data-testid="hero-cta-secondary"
                >
                  Ver productos
                </Button>
              </a>
            </div>
            <p className="mt-4 text-sm text-ink-500">Gratis para empezar. Sin tarjeta.</p>

            {/* Trust bar */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
              <div className="flex items-center gap-2">
                <div className="flex text-coral">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={16} strokeWidth={1.75} fill="currentColor" />
                  ))}
                </div>
                <span className="text-sm text-ink-700 font-medium">4.9</span>
                <span className="text-sm text-ink-500">· Hecho en México</span>
              </div>
              <div className="text-sm text-ink-500 flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.75} className="text-coral" />
                24 plantillas LATAM listas
              </div>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="Amigos organizándose"
                className="w-full h-[440px] object-cover rounded-3xl shadow-lg"
              />
              <div className="absolute -left-6 bottom-8 bg-white border border-line rounded-2xl px-5 py-4 shadow-md">
                <div className="text-xs uppercase tracking-widest text-ink-500">
                  Se apuntó
                </div>
                <div className="font-heading text-lg text-ink-900">
                  María trae guacamole
                </div>
              </div>
              <div className="absolute -right-4 top-8 bg-white border border-line rounded-2xl px-5 py-4 shadow-md">
                <div className="text-xs uppercase tracking-widest text-ink-500">
                  Recaudado
                </div>
                <div className="font-heading text-lg text-ink-900">
                  $12,400 de $20,000
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section
        id="productos"
        className="bg-cream-100 border-y border-line py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.24em] text-coral">
              Elige tu punto de partida
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
              Todo lo que tu grupo necesita, en una sola cuenta.
            </h2>
            <p className="mt-6 text-ink-500 leading-relaxed">
              Nueve productos que resuelven necesidades reales de escuelas, iglesias, equipos y comunidades. Desde una hoja de inscripción hasta una tienda con carrito.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p) => (
              <Link
                key={p.title}
                to={`/productos/${p.slug}`}
                className="bg-white border border-line rounded-2xl p-6 transition-transform duration-200 ease-out hover:-translate-y-1 hover:shadow-md hover:border-coral flex flex-col"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="w-11 h-11 rounded-full bg-cream-100 flex items-center justify-center text-coral">
                  <p.icon size={20} strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-heading text-xl text-ink-900">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-ink-500 leading-relaxed flex-1">
                  {p.tag}
                </p>
                <span className="mt-4 text-sm text-coral inline-flex items-center gap-1">
                  Ver más
                  <ArrowRight size={14} strokeWidth={1.75} />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-12 flex items-center justify-center">
            <Link to="/registro">
              <Button
                variant="outline"
                className="rounded-full border-line hover:bg-white px-6 h-11"
                data-testid="products-cta"
              >
                Empezar a organizar gratis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits split */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 space-y-20">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.24em] text-coral">
            Por qué Apúntate
          </span>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
            Le quita el estrés a organizar.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <h3 className="font-heading text-3xl text-ink-900 font-light">
              Voluntarios y eventos, coordinados sin cadenas de WhatsApp
            </h3>
            <p className="mt-4 text-ink-500 leading-relaxed">
              Crea una hoja, comparte un solo link y deja que la app haga el seguimiento por ti. Sin cupos duplicados, sin llamadas para confirmar, sin app para tus invitados.
            </p>
            <ul className="mt-6 space-y-2 text-ink-700 text-sm">
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Cupos por espacio con conteo en tiempo real
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Los invitados se apuntan sin cuenta
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Panel con quién trae qué o quién falta
              </li>
            </ul>
          </div>
          <img
            src={IMG_VOL}
            alt="Voluntariado coordinado"
            className="w-full h-80 md:h-96 object-cover rounded-3xl shadow-md"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <img
            src={IMG_SCHOOL}
            alt="Escuela y familia"
            className="w-full h-80 md:h-96 object-cover rounded-3xl shadow-md order-2 md:order-1"
          />
          <div className="order-1 md:order-2">
            <h3 className="font-heading text-3xl text-ink-900 font-light">
              Cuotas, donaciones y boletos, todo desde una misma página
            </h3>
            <p className="mt-4 text-ink-500 leading-relaxed">
              Vaquitas, boletos, cuotas, mercancía — recolecta desde el mismo evento sin usar varias herramientas. Empieza gratis y sin comisiones mensuales.
            </p>
            <ul className="mt-6 space-y-2 text-ink-700 text-sm">
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Meta en vivo con barra de progreso
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Boletos con código QR único
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} strokeWidth={1.75} className="text-coral mt-0.5" />
                Registro manual o con Stripe / Mercado Pago
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Use cases (styled as SUG testimonials cards) */}
      <section className="bg-cream-100 border-y border-line py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.24em] text-coral">
              Así lo usan
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
              Organizadores reales, resultados reales.
            </h2>
            <p className="mt-6 text-ink-500 leading-relaxed">
              Escenarios comunes en LATAM donde Apúntate hace el trabajo pesado.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {USE_CASES.map((c, i) => (
              <div
                key={i}
                className="bg-white border border-line rounded-3xl overflow-hidden flex flex-col"
                data-testid={`use-case-${i}`}
              >
                <img
                  src={c.img}
                  alt={c.subject}
                  className="w-full h-56 object-cover"
                />
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs uppercase tracking-widest bg-cream-100 text-ink-700 rounded-full px-3 py-1">
                      {c.stat1}
                    </span>
                    <span className="text-xs uppercase tracking-widest bg-cream-100 text-ink-700 rounded-full px-3 py-1">
                      {c.stat2}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-xl text-ink-900">
                    {c.subject}
                  </h3>
                  <p className="mt-3 text-ink-500 leading-relaxed italic text-sm flex-1">
                    &ldquo;{c.quote}&rdquo;
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Big CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-24">
        <div className="rounded-3xl bg-ink-900 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
          <h2 className="font-heading text-4xl sm:text-5xl font-light leading-tight relative">
            Junta a tu gente.{" "}
            <em className="text-coral not-italic">Que sea sencillo.</em>
          </h2>
          <p className="mt-5 text-ink-300 max-w-2xl relative leading-relaxed">
            Únete a los organizadores que usan Apúntate para planear más rápido, ahorrar tiempo y mover a su comunidad — todo desde una sola plataforma fácil de usar.
          </p>
          <div className="mt-8 relative">
            <Link to="/registro">
              <Button
                size="lg"
                className="rounded-full bg-coral hover:bg-coral-hover text-white px-8 h-12"
                data-testid="cta-bottom-btn"
              >
                Empezar gratis
                <ArrowRight size={18} strokeWidth={1.75} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        <div className="max-w-2xl mb-10">
          <span className="text-xs uppercase tracking-[0.24em] text-coral">
            Preguntas frecuentes
          </span>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
            Lo que todo mundo pregunta.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="bg-white border border-line rounded-2xl px-5 data-[state=open]:border-coral"
              data-testid={`faq-${i}`}
            >
              <AccordionTrigger className="text-left font-heading text-lg text-ink-900 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink-500 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Footer */}
      <footer className="border-t border-line bg-cream-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-coral text-white">
                <Sparkles size={16} strokeWidth={1.75} />
              </span>
              <span className="font-heading text-xl text-ink-900">Apúntate</span>
            </div>
            <p className="mt-4 text-sm text-ink-500 leading-relaxed max-w-sm">
              Inscripciones y pagos para grupos en México y toda LATAM. Todo en un solo lugar, sin app para invitados.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-3">
              Productos
            </div>
            <ul className="space-y-2 text-sm text-ink-700">
              <li><Link to="/productos/hojas-de-inscripcion" className="hover:text-coral">Hojas de inscripción</Link></li>
              <li><Link to="/productos/boletos" className="hover:text-coral">Boletos con QR</Link></li>
              <li><Link to="/productos/regalos-grupales" className="hover:text-coral">Vaquitas</Link></li>
              <li><Link to="/productos/cuotas" className="hover:text-coral">Cuotas</Link></li>
              <li><Link to="/productos/tienda-en-linea" className="hover:text-coral">Tienda</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-3">
              Cuenta
            </div>
            <ul className="space-y-2 text-sm text-ink-700">
              <li><Link to="/login" className="hover:text-coral">Iniciar sesión</Link></li>
              <li><Link to="/registro" className="hover:text-coral">Crear cuenta</Link></li>
              <li><Link to="/recuperar" className="hover:text-coral">Recuperar contraseña</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-line py-6 text-center text-xs text-ink-500">
          Apúntate · Hecho con cariño en México
        </div>
      </footer>
    </div>
  );
}
